/**
 * Synthesizes a representative sample VALUE from a JSON Schema, so the "try it"
 * console can show a populated, realistic-looking sample response before a real run.
 * The normalized output schemas carry no examples, so scalar values are inferred
 * from the field NAME (a "followers" integer -> 12500, a "url" string -> a URL, a
 * "created_at" string -> an ISO date) and fall back to the type otherwise. The
 * envelope, field names, and nesting come straight from the schema, so the shape is
 * exact. A `null` branch of a `T | null` union is skipped so `data` samples a real
 * object, not null.
 */

type Obj = Record<string, unknown>;

const asObj = (v: unknown): Obj | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null;

// Bounds recursion on a self-referential or very deep schema.
const MAX_DEPTH = 6;

/** Builds a realistic sample value matching `schema`. Returns null for an empty one. */
export const sampleFromSchema = (schema: unknown): unknown => build(schema, 0, '');

const build = (schema: unknown, depth: number, name: string): unknown => {
  const s = asObj(schema);
  if (!s || depth > MAX_DEPTH) return null;

  if (s.example !== undefined) return s.example;
  if (s.const !== undefined) return s.const;
  if (s.default !== undefined) return s.default;
  if (Array.isArray(s.enum) && s.enum.length > 0) return s.enum[0];

  // Union: pick the first non-null branch so `T | null` samples a T.
  const union = (s.oneOf ?? s.anyOf) as unknown[] | undefined;
  if (Array.isArray(union) && union.length > 0) {
    const real = union.map(asObj).find((b) => b && b.type !== 'null');
    return build(real ?? union[0], depth, name);
  }

  const types = Array.isArray(s.type) ? s.type : s.type ? [s.type] : [];
  const t = (types.find((x) => x !== 'null') as string | undefined) ?? (s.properties ? 'object' : undefined);

  switch (t) {
    case 'object': {
      const props = asObj(s.properties);
      if (!props) return {};
      const out: Obj = {};
      for (const [key, prop] of Object.entries(props)) out[key] = build(prop, depth + 1, key);
      return out;
    }
    case 'array':
      // Array items inherit the field name so e.g. a "tags" array samples tag-ish strings.
      return asObj(s.items) ? [build(s.items, depth + 1, name)] : [];
    case 'string':
      return sampleString(name);
    case 'integer':
      return sampleNumber(name, true);
    case 'number':
      return sampleNumber(name, false);
    case 'boolean':
      return sampleBoolean(name);
    default:
      return null;
  }
};

/** True when `name` (lowercased) contains any of the given substrings. */
const matches = (name: string, ...keys: string[]): boolean => {
  const lc = name.toLowerCase();
  return keys.some((k) => lc.includes(k));
};

/** A believable string for a field, inferred from its name. */
const sampleString = (name: string): string => {
  if (matches(name, 'avatar', 'image', 'photo', 'thumbnail', 'picture', 'icon', 'logo'))
    return 'https://example.com/image.jpg';
  if (matches(name, 'url', 'link', 'href', 'website', 'permalink', 'source'))
    return 'https://example.com/page';
  if (matches(name, 'email')) return 'alex@example.com';
  if (matches(name, 'phone', 'tel')) return '+1 555-0142';
  if (matches(name, 'domain', 'host')) return 'example.com';
  if (matches(name, 'username', 'handle', 'screen_name', 'login', 'slug')) return 'alex_rivera';
  if (matches(name, 'first_name', 'firstname')) return 'Alex';
  if (matches(name, 'last_name', 'lastname', 'surname')) return 'Rivera';
  if (matches(name, 'author', 'owner', 'display_name', 'displayname', 'full_name', 'fullname'))
    return 'Alex Rivera';
  if (matches(name, 'company', 'brand', 'org', 'business')) return 'Acme Inc';
  if (matches(name, 'description', 'summary', 'bio', 'about', 'caption', 'content', 'text', 'message', 'body', 'snippet', 'excerpt', 'review'))
    return 'A short example description of this item.';
  if (matches(name, 'title', 'headline', 'subject', 'name', 'label')) return 'Example title';
  if (matches(name, 'date', 'time', 'created', 'updated', 'published', 'timestamp')) return '2024-01-15T09:30:00Z';
  if (matches(name, 'country')) return 'US';
  if (matches(name, 'language', 'lang', 'locale')) return 'en';
  if (matches(name, 'currency')) return 'USD';
  if (matches(name, 'city')) return 'San Francisco';
  if (matches(name, 'state', 'region', 'province')) return 'CA';
  if (matches(name, 'address', 'street')) return '123 Main St';
  if (matches(name, 'zip', 'postal')) return '94107';
  if (matches(name, 'category', 'type', 'kind', 'genre', 'industry')) return 'general';
  if (matches(name, 'status', 'state')) return 'active';
  if (matches(name, 'color', 'colour')) return '#1f4fe0';
  if (matches(name, 'price', 'amount', 'cost', 'value')) return '19.99';
  if (matches(name, 'id', 'sku', 'code', 'key', 'token', 'hash')) return 'a1b2c3d4';
  return 'example';
};

/** A believable number for a field, inferred from its name; whole when `int`. */
const sampleNumber = (name: string, int: boolean): number => {
  const pick = (): number => {
    if (matches(name, 'year')) return 2024;
    if (matches(name, 'latitude') || /\blat\b/.test(name.toLowerCase())) return 37.7749;
    if (matches(name, 'longitude', 'lng', 'lon')) return -122.4194;
    if (matches(name, 'rating', 'score', 'stars')) return 4.6;
    if (matches(name, 'percent', 'ratio', 'rate', 'share')) return 12.5;
    if (matches(name, 'price', 'amount', 'cost', 'value', 'balance', 'revenue', 'salary')) return 19.99;
    if (matches(name, 'follower', 'following', 'like', 'view', 'comment', 'share', 'subscriber', 'count', 'total', 'volume', 'traffic', 'visit', 'impression', 'click', 'reach', 'engagement'))
      return 12500;
    if (matches(name, 'review', 'result', 'item', 'post', 'photo', 'video')) return 248;
    if (matches(name, 'duration', 'seconds', 'length')) return 180;
    if (matches(name, 'age')) return 29;
    if (matches(name, 'page', 'index', 'rank', 'position', 'order')) return 1;
    if (matches(name, 'id')) return 1024;
    return int ? 42 : 12.5;
  };
  const v = pick();
  return int ? Math.round(v) : v;
};

/** A believable boolean for a field, inferred from its name. */
const sampleBoolean = (name: string): boolean => {
  if (matches(name, 'private', 'deleted', 'banned', 'disabled', 'hidden', 'archived', 'suspended', 'blocked', 'closed'))
    return false;
  return true;
};
